import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package } from '../types/Package';
import { loadServicesFromServer } from '../utils/ServiceAPI';
import ServiceCard from '../components/Services/ServiceCard';
import SEOHead from '../components/SEO/SEOHead';
import './ServicesPage.css';

const ServicesPage: React.FC = () => {
  const [services, setServices] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const loadServices = async () => {
      try {
        const data = await loadServicesFromServer();
        if (isMounted) {
          setServices(data);
          setLoading(false);
        }
      } catch (e) {
        if (isMounted) {
          console.error('Error loading services:', e);
          setError('Failed to load services');
          setLoading(false);
        }
      }
    };

    loadServices();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleServiceClick = useCallback((service: Package) => {
    // Generate slug from service name if not present
    const slug = service.slug || service.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    // Use setTimeout to ensure navigation happens asynchronously
    setTimeout(() => {
      navigate(`/services/${slug}`);
    }, 0);
  }, [navigate]);

  const specials = services.filter(s => s.isSpecial);
  const packages = services.filter(s => !s.isSpecial);

  if (loading) {
    return (
      <div className="services-page">
        <p className="services-status">Loading services...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="services-page">
        <p className="services-status services-status-error">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="btn btn-primary"
          type="button"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="services-page">
      <SEOHead
        title="Photography Services & Packages"
        description="Explore my professional photography packages tailored to capture your special moments. From portraits to events, I have the perfect package for you."
        keywords={['photography services', 'photography packages', 'portrait packages', 'wedding packages', 'event photography', 'Cape Town photography services']}
        type="website"
      />
      <div className="services-wrap">
        <header className="services-hero">
          <h1 className="elegant-heading">Services</h1>
          <p>Explore packages, limited-time specials, and add-ons. All sessions include gentle guidance and a curated selection of edited images.</p>
        </header>

        {specials.length > 0 && (
          <section className="services-block">
            <h2 className="elegant-heading">Special Offers</h2>
            <div className="services-grid">
              {specials.map(s => (
                <ServiceCard
                  key={s.id}
                  service={s}
                  onClick={handleServiceClick}
                />
              ))}
            </div>
          </section>
        )}

        <section className="services-block">
          <h2 className="elegant-heading">Packages</h2>
          <div className="services-grid">
            {packages.map(s => (
              <ServiceCard
                key={s.id}
                service={s}
                onClick={handleServiceClick}
              />
            ))}
          </div>
        </section>

        {services.length === 0 && (
          <p className="services-status">No services available at this time.</p>
        )}

        <section className="services-block services-notes">
          <h2 className="elegant-heading">How It Works</h2>
          <ol className="services-steps">
            <li>Enquiry and date check</li>
            <li>Planning and styling guidance</li>
            <li>Shoot day</li>
            <li>Editing and delivery</li>
          </ol>
        </section>

        <section className="services-block services-notes">
          <h2 className="elegant-heading">FAQs</h2>
          <div className="services-faq">
            <details>
              <summary>How many photos do we receive?</summary>
              <p>Packages list typical inclusions. You can add more images or time as add-ons.</p>
            </details>
            <details>
              <summary>Where do sessions take place?</summary>
              <p>On location around Cape Town or in studio by arrangement.</p>
            </details>
            <details>
              <summary>Do you help with styling?</summary>
              <p>Yes. You'll receive guidance on outfits and locations that suit your vision.</p>
            </details>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ServicesPage;
